import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { DemoProvider } from "@/contexts/DemoContext";
import { CartProvider } from "@/contexts/CartContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CartDrawer } from "@/components/shop/CartDrawer";
import { ClickSparkle } from "@/components/marketing/ClickSparkle";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SuperAdminRoute } from "@/components/SuperAdminRoute";

// Eagerly loaded: homepage + marketing shell
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy-loaded: all other routes
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Leads = lazy(() => import("./pages/Leads"));
const LeadDetail = lazy(() => import("./pages/LeadDetail"));
const Estimates = lazy(() => import("./pages/Estimates"));
const EstimateBuilder = lazy(() => import("./pages/EstimateBuilder"));
const Production = lazy(() => import("./pages/Production"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Invoices = lazy(() => import("./pages/Invoices"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const Sales = lazy(() => import("./pages/Sales"));
const Team = lazy(() => import("./pages/Team"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const Tools = lazy(() => import("./pages/Tools"));
const Integrations = lazy(() => import("./pages/Integrations"));
const CustomerPortal = lazy(() => import("./pages/CustomerPortal"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const SuperAdmin = lazy(() => import("./pages/SuperAdmin"));
const SuperAdminCompanies = lazy(() => import("./pages/SuperAdminCompanies"));
const SuperAdminUsers = lazy(() => import("./pages/SuperAdminUsers"));
const SuperAdminSettings = lazy(() => import("./pages/SuperAdminSettings"));
const SuperAdminOnboarding = lazy(() => import("./pages/SuperAdminOnboarding"));
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const CityDetail = lazy(() => import("./pages/CityDetail"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Documents = lazy(() => import("./pages/Documents"));
const Stakeholders = lazy(() => import("./pages/Stakeholders"));
const PublicLeadForm = lazy(() => import("./pages/PublicLeadForm"));
const EmailMarketing = lazy(() => import("./pages/EmailMarketing"));
const Sms = lazy(() => import("./pages/Sms"));
const JoinCompany = lazy(() => import("./pages/JoinCompany"));
const Tasks = lazy(() => import("./pages/Tasks"));
const CalendarPage = lazy(() => import("./pages/Calendar"));
const PublicBooking = lazy(() => import("./pages/PublicBooking"));
const ManageAppointment = lazy(() => import("./pages/ManageAppointment"));
const BrandKit = lazy(() => import("./pages/BrandKit"));
const InstantQuote = lazy(() => import("./pages/InstantQuote"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const Orders = lazy(() => import("./pages/Orders"));
const SubServiceDetail = lazy(() => import("./pages/SubServiceDetail"));
const Inventory = lazy(() => import("./pages/Inventory"));
const WebsiteManager = lazy(() => import("./pages/WebsiteManager"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FlowersAndWineGuide = lazy(() => import("./pages/FlowersAndWineGuide"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const AdLandingPage = lazy(() => import("./pages/AdLandingPage"));
const CustomerProposal = lazy(() => import("./pages/CustomerProposal"));
const FloralQuestionnaire = lazy(() => import("./pages/FloralQuestionnaire"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <AuthProvider>
      <DemoProvider>
      <CartProvider>
      <CompanyProvider>
        <TooltipProvider>
          <BrowserRouter>
            <Toaster />
            <Sonner />
            <CartDrawer />
            <ClickSparkle />
            <ScrollToTop />
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/login" element={<Auth />} />
                <Route path="/signup" element={<Auth />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/services/:slug/:subSlug" element={<SubServiceDetail />} />
                <Route path="/service-areas" element={<ServiceAreas />} />
                <Route path="/service-areas/:slug" element={<CityDetail />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/about" element={<About />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/:slug" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/return" element={<CheckoutReturn />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
                <Route path="/gallery" element={<Portfolio />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/category/:categorySlug" element={<Blog />} />
                <Route path="/blog/flowers-and-wine-gift-guide" element={<FlowersAndWineGuide />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/instant-quote" element={<InstantQuote />} />
                <Route path="/consultation" element={<InstantQuote />} />
                <Route path="/book/:slug" element={<PublicBooking />} />
                <Route path="/lp/wedding" element={<AdLandingPage variant="wedding" />} />
                <Route path="/lp/events" element={<AdLandingPage variant="events" />} />
                <Route path="/lp/sympathy" element={<AdLandingPage variant="sympathy" />} />
                <Route path="/floral-questionnaire" element={<FloralQuestionnaire />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/appointment/:token" element={<ManageAppointment />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
                <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
                <Route path="/leads/:id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
                <Route path="/estimates" element={<ProtectedRoute><Estimates /></ProtectedRoute>} />
                <Route path="/estimates/new" element={<ProtectedRoute><EstimateBuilder /></ProtectedRoute>} />
                <Route path="/estimates/:id" element={<ProtectedRoute><EstimateBuilder /></ProtectedRoute>} />
                <Route path="/production" element={<ProtectedRoute><Production /></ProtectedRoute>} />
                <Route path="/production/new" element={<ProtectedRoute><Production /></ProtectedRoute>} />
                <Route path="/production/:id" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
                <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
                <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
                <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
                <Route path="/invoices/:id" element={<ProtectedRoute><InvoiceDetail /></ProtectedRoute>} />
                <Route path="/sales" element={<ProtectedRoute><Sales /></ProtectedRoute>} />
                <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
                <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
                <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
                <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
                <Route path="/brand-kit" element={<ProtectedRoute><BrandKit /></ProtectedRoute>} />
                <Route path="/website-manager" element={<ProtectedRoute><WebsiteManager /></ProtectedRoute>} />
                <Route path="/stakeholders" element={<ProtectedRoute><Stakeholders /></ProtectedRoute>} />
                <Route path="/lead-form" element={<PublicLeadForm />} />
                <Route path="/email-marketing" element={<ProtectedRoute><EmailMarketing /></ProtectedRoute>} />
                <Route path="/sms" element={<ProtectedRoute><Sms /></ProtectedRoute>} />
                <Route path="/portal/proposal/:token" element={<CustomerProposal />} />
                <Route path="/portal/:token" element={<CustomerPortal />} />
                <Route path="/email-preferences/:token" element={<EmailPreferences />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/join/:code" element={<JoinCompany />} />
                <Route path="/join" element={<JoinCompany />} />
                <Route path="/pending-approval" element={<PendingApproval />} />
                <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                <Route path="/super-admin" element={<SuperAdminRoute><SuperAdmin /></SuperAdminRoute>} />
                <Route path="/super-admin/companies" element={<SuperAdminRoute><SuperAdminCompanies /></SuperAdminRoute>} />
                <Route path="/super-admin/users" element={<SuperAdminRoute><SuperAdminUsers /></SuperAdminRoute>} />
                <Route path="/super-admin/onboarding" element={<SuperAdminRoute><SuperAdminOnboarding /></SuperAdminRoute>} />
                <Route path="/super-admin/settings" element={<SuperAdminRoute><SuperAdminSettings /></SuperAdminRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </CompanyProvider>
      </CartProvider>
      </DemoProvider>
    </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
